import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, MenuItem } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { FieldsetModule } from 'primeng/fieldset';
import { forkJoin, Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { FileUploadModule } from 'primeng/fileupload';
import { CommonModule } from '@angular/common';
import { Supplier } from '../../master-data/suppliers/suppliers.model';
import { Business } from '../../master-data/business/business.model';
import { CustomersService } from '../../master-data/customers/customers.service';
import { Customer } from '../../master-data/customers/customers.model';
import { AppUser } from '../../users/user.model';
import { ContractService } from '../contract.service';
import { DatePicker } from 'primeng/datepicker';
import { ContractType } from '../contract-types';
import { ProgressSpinner } from 'primeng/progressspinner';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ChatSession } from '../../models/chat-session.model';
import { ChatSessionService } from '../../services/chat-session.service';
import { MAX_FILE_SIZE_BYTES } from '../../shared/constants/file-size.constants';
import { SPREADSHEET_UPLOAD_ACCEPT_TYPES } from '../../shared/constants/upload-file-types.constants';

@Component({
  selector: 'app-add-edit-contract',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ToastModule,
    CalendarModule,
    DropdownModule,
    InputNumberModule,
    InputTextModule,
    FieldsetModule,
    ButtonModule,
    IftaLabelModule,
    FloatLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    FileUploadModule,
    DatePicker,
    ProgressSpinner,
    ConfirmDialog,
  ],
  providers: [MessageService],
  templateUrl: './contract-finance-form.component.html',
  styleUrls: ['./contract-finance-form.component.css'],
})
export class ContractFinanceFormComponent implements OnInit, OnDestroy {
  readonly maxFileSize = MAX_FILE_SIZE_BYTES;
  readonly spreadsheetAcceptTypes = SPREADSHEET_UPLOAD_ACCEPT_TYPES;
  contractForm!: FormGroup;
  isLoading = false;
  submitted = false;
  isEditMode = false;
  contractId: string | null = null;
  currentUser: AppUser;

  private routeSub!: Subscription;
  private sessionSub!: Subscription;
  currentSessionId: number | null = null;
  currentSession: ChatSession | undefined;

  // Breadcrumb items
  breadcrumbItems: MenuItem[] = [];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  customers: Customer[] = [];
  filteredCustomers: Customer[] = [];
  suppliers: Supplier[] = [];
  suppliersCustomers: Supplier[] = [];

  businesses: Business[] = [];
  filteredBusinesses: Business[] = [];

  contractTypes: { label: string; value: string }[] = Object.entries(
    ContractType,
  )
    .filter(([key]) => key === 'PROGRESSIVE' || key === 'ADJUSTMENT')
    .map(([key, value]) => ({
      label: value,
      value: key,
    }));

  locations: Location[] = [];
  formInfoHolder: FormGroup[] = [];
  isPromoPlansLoading = false;
  yesterdayDate!: Date;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private router: Router,
    private customerService: CustomersService,
    private contractService: ContractService,
    private chatSessionService: ChatSessionService,
    private route: ActivatedRoute,
  ) {
    this.currentUser = JSON.parse(
      sessionStorage.getItem('appUser') ?? '',
    ) as AppUser;
  }

  ngOnInit(): void {
    let routeWithSessionId: ActivatedRoute | null = this.route;
    while (
      routeWithSessionId &&
      !routeWithSessionId.snapshot.paramMap.has('sessionId')
    ) {
      routeWithSessionId = routeWithSessionId.parent;
    }

    this.routeSub = (routeWithSessionId || this.route).paramMap.subscribe(
      (params) => {
        const sessionIdParam = params.get('sessionId');
        if (sessionIdParam) {
          this.currentSessionId = +sessionIdParam;
          this.sessionSub = this.chatSessionService.sessions$.subscribe(
            (sessions) => {
              this.currentSession = sessions.find(
                (s) => s.sessionId === this.currentSessionId,
              );
            },
          );
        } else {
          const errorMsg = 'Session ID not found in route parameters.';
          this.messageService.add({
            severity: 'error',
            summary: 'Routing Error',
            detail: errorMsg,
          });
        }
      },
    );

    this.getYesterdayDate();
    this.contractId = this.route.snapshot.paramMap.get('contractId');
    this.isEditMode = !!this.contractId;

    this.initForm();
    // this.subscribeToValueChanges();

    // Load all necessary dropdown data and then proceed with form population
    this.loadDropdownData().subscribe({
      next: (data) => {
        this.customers = data.customers.content;
        this.filteredCustomers = data.customers.content;
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load required form data.',
        });
        console.error('Error loading dropdown data:', err);
      },
    });
  }

  private initForm(): void {
    this.contractForm = this.fb.group({
      // Contract Request
      contractName: ['', Validators.required],
      // startDateActive: ['', Validators.required],
      // endDateActive: ['', Validators.required],
      // customer: [[], Validators.required],
      contractType: ['', Validators.required],
      note: [''],
      contractFile: [null, Validators.required],
    });
  }

  isInvalid(controlName: string): boolean {
    const control = this.contractForm.get(controlName);
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || this.submitted)
    );
  }

  submitForm() {
    if (this.contractForm.invalid) {
      this.contractForm.markAllAsTouched();
      return;
    }
    const contractFormClone = this.contractForm.value;
    this.submitted = true;
    this.isLoading = true;

    const formData = new FormData();
    formData.append(
      'contract',
      new Blob(
        [
          JSON.stringify({
            contractName: contractFormClone.contractName,
            // startDateActive: contractFormClone.startDateActive
            //   ? this.formatDateToLocal(contractFormClone.startDateActive)
            //   : null,
            // endDateActive: contractFormClone.endDateActive
            //   ? this.formatDateToLocal(contractFormClone.endDateActive)
            //   : null,
            customer: contractFormClone.customer,
            contractType: contractFormClone.contractType,
            contractNote1: contractFormClone.note ?? '',
          }),
        ],
        { type: 'application/json' },
      ),
      'contract.json',
    );

    if (contractFormClone.contractFile) {
      formData.append(
        'contractFile',
        contractFormClone.contractFile,
        contractFormClone.contractFile.name,
      );
    }

    console.log('Session ID:', this.currentSessionId);
    if (this.currentSessionId) {
      formData.append('sessionId', this.currentSessionId.toString());
    }

    this.contractService.createFinanceContract(formData).subscribe({
      next: (res) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Contract created successfully.',
        });
        this.isLoading = false;
        setTimeout(
          () =>
            this.router.navigate([
              '/ai-studio/session/current',
              { outlets: { canvasOutlet: ['rebate', 'list'] } },
            ]),
          1500,
        );
      },
      error: (err) => {
        const detail =
          err.error.message || 'Failed to create progressive rebate  .';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: detail,
        });
        this.isLoading = false;
      },
    });
  }

  getYesterdayDate() {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    this.yesterdayDate = yesterday;
  }

  onFileSelect(event: any): void {
    const file = event.files[0];
    const reader = new FileReader();
    if (file) {
      this.contractForm.get('contractFile')?.setValue(file); // حط الفايل نفسه مش object
    }
    // reader.onload = (e: any) => {
    //   // The result includes the mime type prefix, e.g., "data:image/png;base64,"
    //   const base64String = e.target.result;
    //   this.contractForm.get('contractFile')?.setValue(
    //     this.fb.group({
    //       fileName: [file.name],
    //       fileType: [file.type],
    //       fileSize: [file.size],
    //       fileContent: [base64String], // This is the Base64 string
    //     })
    //   );
    // };
    // reader.readAsDataURL(file);
  }
  downloadTemplate(url: string) {
    const link = document.createElement('a');
    link.href = url;
    link.download = ''; // هينزل بنفس الاسم اللي في الـ URL
    link.click();
  }

  onCancel(): void {
    this.router.navigate(['/contracts']); // Or use Location.back()
  }

  filterCustomers(event: any) {
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredCustomers = this.customers;
    } else {
      this.filteredCustomers = this.customers.filter(
        (customer) =>
          customer.customerName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          customer.customerNumber
            .toLowerCase()
            .includes(event.target.value.toLowerCase()),
      );
    }
  }

  private loadDropdownData() {
    return forkJoin({
      customers: this.customerService.getAllCustomers({
        page: 0,
        size: 10000, //2000
        loadLocations: false,
      }),
    });
  }

  ngOnDestroy(): void {
    // if (this.valueChangesSub) {
    //   this.valueChangesSub.unsubscribe();
    // }
  }

  formatDateToLocal(date: Date | null): string | null {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // JS months = 0-11
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
